/**
 * Image with text – custom carousel (center mode, peek, infinite loop, dots, autoplay).
 * No clones – uses per-slide positioning for a true loop effect.
 * All slides share left:50%; positioning is done purely via translateX.
 * translateX(-50%) = perfectly centered regardless of slide width.
 */
(function () {
  'use strict';

  /* ── Selectors ── */
  var CAROUSEL_SELECTOR = '[data-image-with-text-carousel]';
  var TRACK_SELECTOR    = '.image-with-text__carousel-track';
  var SLIDE_SELECTOR    = '.image-with-text__carousel-slide';
  var ARROWS_SELECTOR   = '.image-with-text__carousel-arrows';
  var ARROW_PREV_SELECTOR = '.image-with-text__carousel-arrow--prev';
  var ARROW_NEXT_SELECTOR = '.image-with-text__carousel-arrow--next';
  var CAPTION_SELECTOR  = '.image-with-text__carousel-caption';
  var CAPTION_TITLE_SELECTOR = '.image-with-text__carousel-caption-title';
  var CAPTION_DESC_SELECTOR  = '.image-with-text__carousel-caption-description';
  var SLIDE_TITLE_SELECTOR   = '.image-with-text__carousel-slide-title';
  var SLIDE_DESC_SELECTOR    = '.image-with-text__carousel-slide-description';
  var SLIDE_LINK_SELECTOR    = '.image-with-text__carousel-slide-link';

  /* ── Constants ── */
  var MOBILE_BP       = 719;
  var MOBILE_SLIDE_PX = 238;
  var DRAG_THRESHOLD  = 8;
  var TRANSITION      = 'transform 0.4s ease, opacity 0.4s ease';

  /* ── Helpers ── */
  function mobile() {
    return window.matchMedia && window.matchMedia('(max-width:' + MOBILE_BP + 'px)').matches;
  }

  function sectionRoot(el) { return el.closest('[data-section-id]') || el; }

  function opt(root, key, fb) {
    var s = sectionRoot(root); if (!s) return fb;
    var v = s.getAttribute('data-carousel-' + key);
    if (v === null || v === '') return fb;
    if (key === 'autoplay') return v === 'true';
    if (key === 'autoplay-delay') return parseInt(v, 10) || 3000;
    return fb;
  }

  /* ══════════════════════════════════════════════════════════════
     Carousel constructor
     ══════════════════════════════════════════════════════════════ */
  function Carousel(container) {
    this.container = container;
    this.track     = container.querySelector(TRACK_SELECTOR);
    this.slides    = Array.from(container.querySelectorAll(SLIDE_SELECTOR));
    this.arrowsCont = container.querySelector(ARROWS_SELECTOR);
    this.arrowPrev  = this.arrowsCont ? this.arrowsCont.querySelector(ARROW_PREV_SELECTOR) : null;
    this.arrowNext  = this.arrowsCont ? this.arrowsCont.querySelector(ARROW_NEXT_SELECTOR) : null;
    this.length     = this.slides.length;
    this.cur       = 0;
    this.autoplayTimer = null;

    /* drag state */
    this.isDragging = false;
    this.hadDrag    = false;
    this.startX     = 0;
    this.currentX   = 0;

    if (!this.track || this.length === 0) return null;

    this.autoplay      = opt(container, 'autoplay', false);
    this.autoplayDelay = Math.max(1000, opt(container, 'autoplay-delay', 3000));

    /* caption panels */
    this.captionEl = container.querySelector(CAPTION_SELECTOR);
    this.panelA    = this.captionEl ? this.captionEl.querySelector('[data-caption-panel="a"]') : null;
    this.panelB    = this.captionEl ? this.captionEl.querySelector('[data-caption-panel="b"]') : null;
    this.activePanel = 'a';

    this.setupLayout();
    this.positionSlides(false);
    this.updateCaption();
    this.attachEvents();
    if (this.autoplay) this.startAutoplay();

    var self = this, timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(function () { self.onResize(); }, 120);
    });
  }

  /* ── Layout: absolute slides, all at left:50%, transform handles rest ── */
  Carousel.prototype.setupLayout = function () {
    var t = this.track;
    t.style.position   = 'relative';
    t.style.display    = 'block';
    t.style.overflow   = 'visible';
    t.style.transform  = 'none';
    t.style.transition = 'none';
    t.style.width      = '100%';

    var m = mobile();
    for (var i = 0; i < this.length; i++) {
      var s = this.slides[i];
      s.style.position  = 'absolute';
      s.style.top       = '0';
      s.style.left      = '50%';       /* FIXED – never changes */
      s.style.width     = m ? MOBILE_SLIDE_PX + 'px' : '60%';
      s.style.boxSizing = 'border-box';
    }
  };

  Carousel.prototype.onResize = function () {
    var m = mobile();
    for (var i = 0; i < this.length; i++) {
      this.slides[i].style.width = m ? MOBILE_SLIDE_PX + 'px' : '60%';
    }
    this.positionSlides(false);
  };

  /* ── Relative index with wrapping (shortest path) ── */
  Carousel.prototype.relIndex = function (i, from) {
    if (from === undefined) from = this.cur;
    var d = i - from;
    if (d >  this.length / 2) d -= this.length;
    if (d < -this.length / 2) d += this.length;
    return d;
  };

  /* ── Build CSS transform for a given relative position ──
     All slides have left:50%.
     Desktop : translateX(-50%) = centered.  Each rel unit = 100% of element width.
     Mobile  : translateX(-50%) = centered.  Each rel unit = 238 px.
     Formula : translateX( (-50 + rel*100)% )           desktop
               translateX( calc(-50% + rel*238px) )     mobile                     */
  Carousel.prototype.slideTx = function (rel, dragPx) {
    var dp = dragPx || 0;
    if (mobile()) {
      var px = rel * MOBILE_SLIDE_PX + dp;
      if (px === 0) return 'translateX(-50%)';
      return 'translateX(calc(-50% + ' + px + 'px))';
    }
    var pct = -50 + rel * 100;
    if (dp) return 'translateX(calc(' + pct + '% + ' + dp + 'px))';
    return 'translateX(' + pct + '%)';
  };

  /* ── Position every slide around currentIndex ── */
  Carousel.prototype.positionSlides = function (animate, dragPx) {
    for (var i = 0; i < this.length; i++) {
      var rel = this.relIndex(i);
      var s   = this.slides[i];

      s.style.transition = animate ? TRANSITION : 'none';

      if (Math.abs(rel) <= 1) {
        s.style.opacity       = '1';
        s.style.pointerEvents = '';
        s.style.visibility    = 'visible';
        s.style.zIndex        = rel === 0 ? '2' : '1';
        s.style.transform     = this.slideTx(rel, dragPx);
      } else {
        s.style.opacity       = '0';
        s.style.pointerEvents = 'none';
        s.style.visibility    = 'hidden';
        s.style.zIndex        = '0';
        s.style.transform     = this.slideTx(rel > 0 ? 2 : -2);
      }
    }
  };

  /* ── goTo with wrap‑slide preparation ── */
  Carousel.prototype.goTo = function (index) {
    if (this.length === 0) return;
    var prev = this.cur;
    var next = ((index % this.length) + this.length) % this.length;
    if (next === prev && this.length > 1) return;

    var dir = 0;
    if (next !== prev) {
      if (prev === this.length - 1 && next === 0) dir = 1;
      else if (prev === 0 && next === this.length - 1) dir = -1;
      else dir = next > prev ? 1 : -1;
    }

    this.cur = next;

    /* Phase 1: instantly reposition slides that are "wrapping" into view */
    for (var i = 0; i < this.length; i++) {
      var oldRel = this.relIndex(i, prev);
      var newRel = this.relIndex(i, next);
      var entering = Math.abs(newRel) <= 1 && Math.abs(oldRel) > 1;
      if (entering) {
        var s = this.slides[i];
        s.style.transition  = 'none';
        s.style.visibility  = 'visible';
        s.style.opacity     = '1';
        s.style.transform   = this.slideTx(newRel > 0 ? 2 : -2);
      }
    }
    this.track.offsetHeight; /* force reflow */

    /* Phase 2: animate all slides to their new positions */
    this.positionSlides(true);
    this.updateCaption();
  };

  /* ── Caption (title + description): actualización directa para que título y texto se acomoden a la vez sin delay ── */
  Carousel.prototype.updateCaption = function () {
    if (!this.panelA) return;
    var slide = this.slides[this.cur];
    if (!slide) return;
    var tEl = slide.querySelector(SLIDE_TITLE_SELECTOR);
    var dEl = slide.querySelector(SLIDE_DESC_SELECTOR);
    var newTitle = tEl ? tEl.textContent : '';
    var newDesc  = dEl ? dEl.innerHTML : '';

    var t = this.panelA.querySelector(CAPTION_TITLE_SELECTOR);
    var d = this.panelA.querySelector(CAPTION_DESC_SELECTOR);
    if (t) t.textContent = newTitle;
    if (d) d.innerHTML   = newDesc;

    /* Mantener panel B en sync por si el tema lo usa; ocultar el panel B para que solo se vea A */
    if (this.panelB) {
      var t2 = this.panelB.querySelector(CAPTION_TITLE_SELECTOR);
      var d2 = this.panelB.querySelector(CAPTION_DESC_SELECTOR);
      if (t2) t2.textContent = newTitle;
      if (d2) d2.innerHTML   = newDesc;
      this.panelB.classList.add('image-with-text__carousel-caption-content--hidden');
    }
    this.panelA.classList.remove('image-with-text__carousel-caption-content--hidden');
  };

  /* ── Navigation ── */
  Carousel.prototype.next = function () { this.goTo(this.cur + 1); };
  Carousel.prototype.prev = function () { this.goTo(this.cur - 1); };

  /* ── Autoplay ── */
  Carousel.prototype.startAutoplay = function () {
    var self = this; this.stopAutoplay();
    this.autoplayTimer = setInterval(function () { self.next(); }, this.autoplayDelay);
  };
  Carousel.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) { clearInterval(this.autoplayTimer); this.autoplayTimer = null; }
  };

  /* ── Events ── */
  Carousel.prototype.attachEvents = function () {
    var self = this;

    /* Arrows */
    if (this.arrowPrev) {
      this.arrowPrev.addEventListener('click', function () {
        self.prev();
        if (self.autoplay) { self.stopAutoplay(); self.startAutoplay(); }
      });
    }
    if (this.arrowNext) {
      this.arrowNext.addEventListener('click', function () {
        self.next();
        if (self.autoplay) { self.stopAutoplay(); self.startAutoplay(); }
      });
    }

    /* Block link click after drag */
    this.container.addEventListener('click', function (e) {
      if (!self.hadDrag) return;
      var link = e.target.closest ? e.target.closest(SLIDE_LINK_SELECTOR) : null;
      if (link) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* ── Touch ── */
    this.container.addEventListener('touchstart', function (e) {
      self.startX  = e.touches[0].clientX;
      self.currentX = self.startX;
      self.isDragging = true;
      self.hadDrag = false;
      self.stopAutoplay();
    }, { passive: true });

    this.container.addEventListener('touchmove', function (e) {
      if (!self.isDragging) return;
      self.currentX = e.touches[0].clientX;
      if (Math.abs(self.currentX - self.startX) > DRAG_THRESHOLD) self.hadDrag = true;
      if (self.hadDrag) self.positionSlides(false, self.currentX - self.startX);
    }, { passive: true });

    this.container.addEventListener('touchend', function () {
      if (!self.isDragging) return;
      self.finishDrag();
    });

    /* ── Mouse ── */
    var onMove = function (e) {
      if (!self.isDragging) return;
      self.currentX = e.clientX;
      if (Math.abs(self.currentX - self.startX) > DRAG_THRESHOLD) self.hadDrag = true;
      if (self.hadDrag) self.positionSlides(false, self.currentX - self.startX);
    };
    var onUp = function (e) {
      if (e.button !== 0 || !self.isDragging) return;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      self.container.classList.remove('image-with-text__carousel--dragging');
      self.finishDrag();
    };

    (this.track || this.container).addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      self.startX   = e.clientX;
      self.currentX = e.clientX;
      self.isDragging = true;
      self.hadDrag = false;
      self.stopAutoplay();
      self.container.classList.add('image-with-text__carousel--dragging');
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      e.preventDefault();
    });
  };

  Carousel.prototype.finishDrag = function () {
    var diff = this.startX - this.currentX;
    var threshold = 50;
    if (diff > threshold)       this.next();
    else if (diff < -threshold) this.prev();
    else                        this.positionSlides(true);
    this.isDragging = false;
    if (this.autoplay) this.startAutoplay();
  };

  /* ── Destroy ── */
  Carousel.prototype.destroy = function () {
    this.stopAutoplay();
    for (var i = 0; i < this.length; i++) {
      var s = this.slides[i];
      s.style.position = ''; s.style.top = ''; s.style.left = '';
      s.style.width = ''; s.style.transform = '';
      s.style.transition = ''; s.style.opacity = ''; s.style.visibility = '';
      s.style.zIndex = ''; s.style.pointerEvents = ''; s.style.boxSizing = '';
    }
    this.track.style.position = ''; this.track.style.display = '';
    this.track.style.overflow = ''; this.track.style.transform = '';
    this.track.style.transition = ''; this.track.style.width = '';
  };

  /* ══════════════════════════════════════════════════════════════
     Bootstrap
     ══════════════════════════════════════════════════════════════ */
  function init(container) {
    var el = typeof container.querySelector === 'function'
      ? container.querySelector(CAROUSEL_SELECTOR) : null;
    if (!el) el = container.matches && container.matches(CAROUSEL_SELECTOR) ? container : null;
    if (!el) return null;
    if (el._imageWithTextCarousel) {
      try { el._imageWithTextCarousel.destroy(); } catch (e) {}
      el._imageWithTextCarousel = null;
    }
    var inst = new Carousel(el);
    if (!inst || !inst.track) return null;
    el._imageWithTextCarousel = inst;
    return inst;
  }

  function initAll() {
    var els = document.querySelectorAll(CAROUSEL_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      if (els[i]._imageWithTextCarousel) continue;
      init(els[i]);
    }
  }

  function run() { try { initAll(); } catch (e) { console.warn('IWT Carousel:', e); } }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  window.addEventListener('load', run);
  setTimeout(run, 100);
  setTimeout(run, 500);

  if (typeof MutationObserver !== 'undefined' && document.body) {
    new MutationObserver(function (muts) {
      for (var m = 0; m < muts.length; m++) {
        var nodes = muts[m].addedNodes;
        for (var n = 0; n < nodes.length; n++) {
          var nd = nodes[n]; if (nd.nodeType !== 1) continue;
          if ((nd.matches && nd.matches(CAROUSEL_SELECTOR)) ||
              (nd.querySelector && nd.querySelector(CAROUSEL_SELECTOR))) { run(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector(CAROUSEL_SELECTOR)) init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var c = e.target.querySelector(CAROUSEL_SELECTOR);
    if (c && c._imageWithTextCarousel) { c._imageWithTextCarousel.destroy(); c._imageWithTextCarousel = null; }
  });

  window.ImageWithTextCarousel = { init: init, initAll: initAll };
})();
